<template>
  <div class="import-container">
    <!-- Sub-tabs pour les deux modes d'import JSON -->
    <ul class="nav nav-pills mb-3">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: importMode === 'textarea' }"
          href="#"
          @click.prevent="importMode = 'textarea'"
        >
          <i class="bi bi-textarea-t"></i>
          Paste JSON
        </a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{ active: importMode === 'file' }"
          href="#"
          @click.prevent="importMode = 'file'"
        >
          <i class="bi bi-file-earmark-arrow-up"></i>
          Import JSON file
        </a>
      </li>
    </ul>

    <!-- Import JSON via Textarea -->
    <div v-if="importMode === 'textarea'" class="import-json p-3">
      <h4 class="mb-4">
        <i class="bi bi-file-earmark-arrow-down"></i>
        Import JSON format items
      </h4>

      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <h6><i class="bi bi-info-circle"></i> Instructions:</h6>
        <ul class="mb-0">
          <li>Paste an JSON array with one or many items</li>
          <li>It must be an array: <code>[{...}, {...}]</code></li>
          <li>Each item must have at least: <code>el_Type</code>, <code>el_Text</code></li>
          <li>Items will be checked before importation</li>
        </ul>
      </div>

      <!-- Textarea pour JSON -->
      <div class="mb-3">
        <label class="form-label">
          <strong>JSON Array:</strong>
        </label>
        <textarea
          v-model="jsonInput"
          class="form-control font-monospace"
          rows="15"
          placeholder='[
  {
    "el_Type": "choice",
    "el_Text": "Question 1",
    "el_RichText": "",
    "choices": [...],
    ...
  },
  {
    "el_Type": "textGaps",
    "el_Text": "Question 2",
    ...
  }
]'
          @input="clearError"
        ></textarea>
        <small class="text-muted">Format: Array JSON contenant des items</small>
      </div>

      <!-- Message d'erreur -->
      <div v-if="errorMessage" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle"></i>
        {{ errorMessage }}
      </div>

      <!-- Warnings -->
      <div v-if="validationWarnings.length > 0" class="alert alert-warning mb-3">
        <i class="bi bi-exclamation-triangle"></i>
        <strong>{{ validationWarnings.length }}</strong> avertissement(s):
        <ul class="mb-0 mt-2" style="max-height: 200px; overflow-y: auto;">
          <li v-for="(warning, index) in validationWarnings" :key="index" class="small">
            {{ warning }}
          </li>
        </ul>
      </div>

      <!-- Preview des items détectés -->
      <div v-if="parsedItems.length > 0" class="alert alert-success mb-3">
        <i class="bi bi-check-circle"></i>
        <strong>{{ parsedItems.length }}</strong> item(s) validé(s) et prêt(s) à importer:
        <ul class="mb-0 mt-2" style="max-height: 200px; overflow-y: auto;">
          <li v-for="(item, index) in parsedItems" :key="index">
            <strong>{{ item.el_Type }}</strong> - {{ item.el_Text || 'Sans titre' }}
            <span class="badge bg-secondary ms-2">Score: {{ item.maxScore }}</span>
          </li>
        </ul>
      </div>

      <!-- Boutons d'action -->
      <div class="d-flex gap-2">
        <button
          @click="validateJSON"
          class="btn btn-primary"
          :disabled="!jsonInput.trim()"
        >
          <i class="bi bi-search"></i>
          Verify JSON
        </button>
        <button
          @click="importItems"
          class="btn btn-success"
          :disabled="parsedItems.length === 0"
        >
          <i class="bi bi-upload"></i>
          Import {{ parsedItems.length }} item(s)
        </button>
        <button @click="clearAll" class="btn btn-secondary">
          <i class="bi bi-x-circle"></i>
          Clear
        </button>
      </div>
    </div>

    <!-- Import JSON via Fichier -->
    <div v-if="importMode === 'file'" class="import-json p-3">
      <h4 class="mb-4">
        <i class="bi bi-file-earmark-arrow-up"></i>
        Importer un fichier JSON
      </h4>

      <!-- Instructions -->
      <div class="alert alert-info mb-4">
        <h6><i class="bi bi-info-circle"></i> Instructions:</h6>
        <ul class="mb-0">
          <li>Sélectionnez un fichier .json contenant un array d'items</li>
          <li>Le format doit être un tableau: <code>[{...}, {...}]</code></li>
          <li>Chaque item doit avoir au minimum: <code>el_Type</code>, <code>el_Text</code></li>
          <li>Le fichier sera automatiquement validé</li>
        </ul>
      </div>

      <!-- File Input -->
      <div class="mb-3">
        <label class="form-label">
          <strong>Sélectionner un fichier JSON:</strong>
        </label>
        <input
          type="file"
          ref="fileInput"
          accept=".json,application/json"
          @change="handleFileUpload"
          class="form-control"
        />
        <small class="text-muted">Format: .json uniquement</small>
      </div>

      <!-- Nom du fichier chargé -->
      <div v-if="uploadedFileName" class="alert alert-secondary mb-3">
        <i class="bi bi-file-earmark-check"></i>
        Fichier chargé: <strong>{{ uploadedFileName }}</strong>
        <button @click="clearFile" class="btn btn-sm btn-link text-danger float-end">
          <i class="bi bi-x-circle"></i> Supprimer
        </button>
      </div>

      <!-- Message d'erreur -->
      <div v-if="errorMessage" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle"></i>
        {{ errorMessage }}
      </div>

      <!-- Warnings -->
      <div v-if="validationWarnings.length > 0" class="alert alert-warning mb-3">
        <i class="bi bi-exclamation-triangle"></i>
        <strong>{{ validationWarnings.length }}</strong> avertissement(s):
        <ul class="mb-0 mt-2" style="max-height: 200px; overflow-y: auto;">
          <li v-for="(warning, index) in validationWarnings" :key="index" class="small">
            {{ warning }}
          </li>
        </ul>
      </div>

      <!-- Preview des items détectés -->
      <div v-if="parsedItems.length > 0" class="alert alert-success mb-3">
        <i class="bi bi-check-circle"></i>
        <strong>{{ parsedItems.length }}</strong> item(s) validé(s) et prêt(s) à importer:
        <ul class="mb-0 mt-2" style="max-height: 200px; overflow-y: auto;">
          <li v-for="(item, index) in parsedItems" :key="index">
            <strong>{{ item.el_Type }}</strong> - {{ item.el_Text || 'Sans titre' }}
            <span class="badge bg-secondary ms-2">Score: {{ item.maxScore }}</span>
          </li>
        </ul>
      </div>

      <!-- Boutons d'action -->
      <div class="d-flex gap-2">
        <button
          @click="importItems"
          class="btn btn-success"
          :disabled="parsedItems.length === 0"
        >
          <i class="bi bi-upload"></i>
          Importer {{ parsedItems.length }} item(s)
        </button>
        <button @click="clearAll" class="btn btn-secondary">
          <i class="bi bi-x-circle"></i>
          Tout effacer
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { interactions } from '@/config/interactions';

const emit = defineEmits(['confirm', 'cancel', 'items-imported']);

// Mode d'import : 'textarea' ou 'file'
const importMode = ref('textarea');

// Pour l'import JSON
const jsonInput = ref('');
const parsedItems = ref([]);
const errorMessage = ref('');
const validationWarnings = ref([]);

// Pour l'import fichier
const fileInput = ref(null);
const uploadedFileName = ref('');

// Types d'interactions autorisés
const ALLOWED_TYPES = Object.keys(interactions);

// Champs obligatoires pour tous les items
const REQUIRED_FIELDS = ['el_Type', 'el_Text'];

// Champs recommandés
const RECOMMENDED_FIELDS = ['el_RichText', 'maxScore', 'difficulty_rating'];

// === Fonctions de validation rigoureuse ===

// Valider un item individuel
const validateItem = (item, index) => {
  const errors = [];
  const warnings = [];

  // 1. Vérifier les champs obligatoires
  for (const field of REQUIRED_FIELDS) {
    if (!item[field]) {
      errors.push(`Item ${index + 1}: Champ obligatoire "${field}" manquant`);
    }
  }

  // 2. Vérifier que el_Type est valide
  if (item.el_Type && !ALLOWED_TYPES.includes(item.el_Type)) {
    errors.push(`Item ${index + 1}: Type "${item.el_Type}" non reconnu. Types valides: ${ALLOWED_TYPES.join(', ')}`);
  }

  // 3. Vérifier les champs recommandés
  for (const field of RECOMMENDED_FIELDS) {
    if (item[field] === undefined || item[field] === null) {
      warnings.push(`Item ${index + 1}: Champ recommandé "${field}" manquant`);
    }
  }

  // 4. Validation spécifique par type
  if (item.el_Type) {
    const typeErrors = validateByType(item, index);
    errors.push(...typeErrors);
  }

  // 5. Vérifier les types de données
  if (item.maxScore !== undefined && typeof item.maxScore !== 'number') {
    errors.push(`Item ${index + 1}: "maxScore" doit être un nombre`);
  }

  if (item.difficulty_rating !== undefined && typeof item.difficulty_rating !== 'number') {
    errors.push(`Item ${index + 1}: "difficulty_rating" doit être un nombre`);
  }

  // 6. Vérifier que el_ID est valide (si présent)
  if (item.el_ID && (typeof item.el_ID !== 'string' || item.el_ID.trim() === '')) {
    errors.push(`Item ${index + 1}: "el_ID" doit être une chaîne non vide`);
  }

  // 7. Vérifier les champs suspects (pollution potentielle)
  const suspiciousFields = ['_id', '__v', 'createdAt', 'updatedAt'];
  for (const field of suspiciousFields) {
    if (item[field]) {
      warnings.push(`Item ${index + 1}: Champ suspect "${field}" détecté (sera ignoré)`);
      delete item[field]; // Nettoyer automatiquement
    }
  }

  return { errors, warnings };
};

// Validation spécifique par type d'interaction
const validateByType = (item, index) => {
  const errors = [];

  switch (item.el_Type) {
    case 'choice':
    case 'choiceBlock':
      if (!item.options || !Array.isArray(item.options)) {
        errors.push(`Item ${index + 1}: Type "${item.el_Type}" requiert un tableau "options"`);
      } else if (item.options.length < 2) {
        errors.push(`Item ${index + 1}: "options" doit contenir au moins 2 choix`);
      }
      break;

    case 'textGaps':
      if (!item.gaps || !Array.isArray(item.gaps)) {
        errors.push(`Item ${index + 1}: Type "textGaps" requiert un tableau "gaps"`);
      }
      break;

    case 'match':
      if (!item.el_Data) {
        errors.push(`Item ${index + 1}: Type "match" requiert "el_Data"`);
      } else {
        if (!item.el_Data.rows || !Array.isArray(item.el_Data.rows)) {
          errors.push(`Item ${index + 1}: "el_Data.rows" requis pour type "match"`);
        }
        if (!item.el_Data.columns || !Array.isArray(item.el_Data.columns)) {
          errors.push(`Item ${index + 1}: "el_Data.columns" requis pour type "match"`);
        }
        if (!item.el_Data.solutionMatrix || !Array.isArray(item.el_Data.solutionMatrix)) {
          errors.push(`Item ${index + 1}: "el_Data.solutionMatrix" requis pour type "match"`);
        }
      }
      break;

    case 'makingPairs':
      if (!item.options || !Array.isArray(item.options)) {
        errors.push(`Item ${index + 1}: Type "makingPairs" requiert un tableau "options"`);
      }
      break;

    case 'order':
      if (!item.labels || !Array.isArray(item.labels)) {
        errors.push(`Item ${index + 1}: Type "order" requiert un tableau "labels"`);
      } else if (item.labels.length < 2) {
        errors.push(`Item ${index + 1}: "labels" doit contenir au moins 2 éléments`);
      }
      break;

    case 'hotSpot':
      if (!item.hotspotConfig) {
        errors.push(`Item ${index + 1}: Type "hotSpot" requiert "hotspotConfig"`);
      }
      break;

    case 'geogebra':
      if (!item.geogebraConfig) {
        errors.push(`Item ${index + 1}: Type "geogebra" requiert "geogebraConfig"`);
      }
      break;

    case 'graphicChoice':
      if (!item.graphicChoiceConfig) {
        errors.push(`Item ${index + 1}: Type "graphicChoice" requiert "graphicChoiceConfig"`);
      }
      break;

    case 'shortAnswer':
    case 'textPuzzle':
    case 'message':
      // Ces types n'ont pas de validation spécifique stricte
      break;
  }

  return errors;
};

// === Fonctions pour l'import JSON ===

// Valider le JSON
const validateJSON = () => {
  errorMessage.value = '';
  parsedItems.value = [];
  validationWarnings.value = [];

  if (!jsonInput.value.trim()) {
    errorMessage.value = 'Veuillez entrer du JSON';
    return;
  }

  try {
    const parsed = JSON.parse(jsonInput.value);

    // Vérifier que c'est un array
    if (!Array.isArray(parsed)) {
      errorMessage.value = 'Le JSON doit être un tableau (array)';
      return;
    }

    // Vérifier que le tableau n'est pas vide
    if (parsed.length === 0) {
      errorMessage.value = 'Le tableau JSON est vide';
      return;
    }

    // Vérifier la taille du tableau (limite de sécurité)
    if (parsed.length > 100) {
      errorMessage.value = `Trop d'items à importer (${parsed.length}). Maximum autorisé: 100 items.`;
      return;
    }

    // Valider chaque item avec le validateur rigoureux
    const validItems = [];
    const allWarnings = [];

    for (let i = 0; i < parsed.length; i++) {
      const item = parsed[i];

      // 1. ENRICHISSEMENT : Ajouter les valeurs par défaut AVANT la validation
      // FORCER un ID unique pour chaque item pour éviter les doublons
      // (même si l'item a déjà un el_ID, on le remplace pour garantir l'unicité)
      item.el_ID = uuidv4();

      // Ajouter el_Text si manquant
      if (!item.el_Text) {
        item.el_Text = item.el_Label || `Item ${i + 1}`;
      }

      // Ajouter des valeurs par défaut pour les champs recommandés manquants
      if (item.maxScore === undefined) {
        item.maxScore = 1;
      }
      if (item.difficulty_rating === undefined) {
        item.difficulty_rating = 5;
      }
      if (!item.el_RichText) {
        item.el_RichText = '';
      }

      // Ajouter metadata
      if (!item.metadata) {
        item.metadata = {};
      }
      item.metadata.source = 'ImportJSON';
      item.metadata.LastModif = new Date().toISOString();

      // 2. VALIDATION : Valider après enrichissement
      const { errors, warnings } = validateItem(item, i);

      // Si des erreurs critiques, arrêter immédiatement
      if (errors.length > 0) {
        errorMessage.value = errors.join('\n');
        return;
      }

      // Collecter les warnings (il ne devrait plus y en avoir pour les champs recommandés)
      allWarnings.push(...warnings);

      validItems.push(item);
    }

    parsedItems.value = validItems;
    validationWarnings.value = allWarnings;

    console.log('✅ JSON validé:', validItems);
    if (allWarnings.length > 0) {
      console.warn('⚠️ Warnings:', allWarnings);
    }

  } catch (error) {
    errorMessage.value = `Erreur de parsing JSON: ${error.message}`;
    console.error('❌ Erreur parsing:', error);
  }
};

// Importer les items
const importItems = () => {
  if (parsedItems.value.length === 0) {
    errorMessage.value = 'Aucun item à importer. Validez d\'abord le JSON.';
    return;
  }

  console.log('📦 Importation de', parsedItems.value.length, 'item(s)');

  // Émettre les items au parent
  emit('confirm', {
    items: parsedItems.value,
    action: 'import'
  });

  emit('items-imported', parsedItems.value);
};

// Effacer tout
const clearAll = () => {
  jsonInput.value = '';
  parsedItems.value = [];
  errorMessage.value = '';
  validationWarnings.value = [];
  uploadedFileName.value = '';
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Effacer l'erreur lors de la saisie
const clearError = () => {
  if (errorMessage.value) {
    errorMessage.value = '';
    parsedItems.value = [];
    validationWarnings.value = [];
  }
};

// === Fonctions pour l'import fichier ===

// Gérer l'upload de fichier
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Vérifier que c'est un fichier JSON
  if (!file.name.endsWith('.json') && file.type !== 'application/json') {
    errorMessage.value = 'Veuillez sélectionner un fichier .json';
    return;
  }

  // Vérifier la taille du fichier (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    errorMessage.value = 'Fichier trop volumineux (max 5MB)';
    return;
  }

  uploadedFileName.value = file.name;
  errorMessage.value = '';
  parsedItems.value = [];
  validationWarnings.value = [];

  // Lire le fichier
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const content = e.target.result;
      jsonInput.value = content; // Stocker le contenu aussi

      // Valider le JSON
      const parsed = JSON.parse(content);

      // Vérifier que c'est un array
      if (!Array.isArray(parsed)) {
        errorMessage.value = 'Le fichier JSON doit contenir un tableau (array)';
        return;
      }

      // Vérifier que le tableau n'est pas vide
      if (parsed.length === 0) {
        errorMessage.value = 'Le tableau JSON est vide';
        return;
      }

      // Vérifier la taille du tableau (limite de sécurité)
      if (parsed.length > 100) {
        errorMessage.value = `Trop d'items à importer (${parsed.length}). Maximum autorisé: 100 items.`;
        return;
      }

      // Valider chaque item avec le validateur rigoureux
      const validItems = [];
      const allWarnings = [];

      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];

        // 1. ENRICHISSEMENT : Ajouter les valeurs par défaut AVANT la validation
        // FORCER un ID unique pour chaque item pour éviter les doublons
        // (même si l'item a déjà un el_ID, on le remplace pour garantir l'unicité)
        item.el_ID = uuidv4();

        // Ajouter el_Text si manquant
        if (!item.el_Text) {
          item.el_Text = item.el_Label || `Item ${i + 1}`;
        }

        // Ajouter des valeurs par défaut pour les champs recommandés manquants
        if (item.maxScore === undefined) {
          item.maxScore = 1;
        }
        if (item.difficulty_rating === undefined) {
          item.difficulty_rating = 5;
        }
        if (!item.el_RichText) {
          item.el_RichText = '';
        }

        // Ajouter metadata
        if (!item.metadata) {
          item.metadata = {};
        }
        item.metadata.source = 'ImportJSON';
        item.metadata.LastModif = new Date().toISOString();

        // 2. VALIDATION : Valider après enrichissement
        const { errors, warnings } = validateItem(item, i);

        // Si des erreurs critiques, arrêter immédiatement
        if (errors.length > 0) {
          errorMessage.value = errors.join('\n');
          return;
        }

        // Collecter les warnings (il ne devrait plus y en avoir pour les champs recommandés)
        allWarnings.push(...warnings);

        validItems.push(item);
      }

      parsedItems.value = validItems;
      validationWarnings.value = allWarnings;

      console.log('✅ Fichier JSON validé:', validItems);
      if (allWarnings.length > 0) {
        console.warn('⚠️ Warnings:', allWarnings);
      }

    } catch (error) {
      errorMessage.value = `Erreur de lecture du fichier: ${error.message}`;
      console.error('❌ Erreur lecture fichier:', error);
    }
  };

  reader.onerror = () => {
    errorMessage.value = 'Erreur lors de la lecture du fichier';
  };

  reader.readAsText(file);
};

// Supprimer le fichier chargé
const clearFile = () => {
  uploadedFileName.value = '';
  parsedItems.value = [];
  errorMessage.value = '';
  validationWarnings.value = [];
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};
</script>

<style scoped>
.import-container {
  max-width: 1000px;
}

.import-json {
  background-color: #f8f9fa;
  border-radius: 0.5rem;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9rem;
}

.alert ul {
  padding-left: 1.5rem;
}

.gap-2 {
  gap: 0.5rem;
}

.nav-pills .nav-link {
  color: #495057;
}

.nav-pills .nav-link.active {
  background-color: #007bff;
  color: white;
}
</style>
